import { propsLeagues } from "@/components/fantasy-props/model";
import { buildCanonicalFingerprint } from "@/lib/dismissal-fingerprints";
import { toBetLinkSet, type BetLinkSet } from "@/lib/bet-links";
import { gameTimeMatcher } from "@/lib/game-date";
import { searchMatcher } from "@/lib/odds-screen/live/derive";
import type { BetLinks, Catalog, DiscrepanciesView, DiscrepancyBook, DiscrepancyRow, DiscrepancySide, EventHeader, LeagueId, RowId } from "@/lib/odds-screen/live/types";
import { periodCase } from "@/lib/market-label";
import type { DiscrepancyFilters } from "./filters";

const LINE_TOLERANCE = 1e-9;
const PRIZEPICKS = "prizepicks";
const FANTASY_POINTS_MARKET = /\bfantasy[\s_-]*points?\b/i;

const EXCLUDED_LEAGUES: ReadonlySet<LeagueId> = new Set(["soccer"]);

export function discrepancyLeagues(catalog: Catalog | null, selected: readonly LeagueId[]) {
  return propsLeagues(catalog, selected, true).filter((league) => !EXCLUDED_LEAGUES.has(league.id));
}

export function bookMultiplier(book: DiscrepancyBook): number | null {
  return book.multiplier !== null && Math.abs(book.multiplier - 1) > LINE_TOLERANCE ? book.multiplier : null;
}

export function sideOdds(book: DiscrepancyBook, side: DiscrepancySide): number | null {
  return side === "over" ? book.overOdds : book.underOdds;
}

export function sideLinks(book: DiscrepancyBook, side: DiscrepancySide): BetLinkSet | null {
  const links = side === "over" ? book.overLinks : book.underLinks;
  return toBetLinkSet(links[0], links[1], links[2]);
}

export type DerivedBook = DiscrepancyBook & { readonly bettable: boolean };

const NO_LINKS: BetLinks = ["", "", ""];

export type BestPlay = {
  readonly book: DerivedBook;
  readonly side: DiscrepancySide;
  readonly odds: number | null;
  readonly links: BetLinkSet | null;
};

export type DerivedRow = {
  readonly row: DiscrepancyRow;
  readonly event: EventHeader;
  readonly books: readonly DerivedBook[];
  readonly modal: number;
  readonly min: number;
  readonly max: number;
  readonly spread: number;
  readonly edgePct: number;
  readonly best: BestPlay;
};

const EDGE_PCT_CAP = 999;

export function spreadEdgePct(min: number, max: number): number {
  const spread = max - min;
  return min !== 0 && spread > 0 ? Math.min((spread / Math.abs(min)) * 100, EDGE_PCT_CAP) : 0;
}

export function edgeRange(min: number, max: number, line: number, side: DiscrepancySide): readonly [number, number] {
  return side === "over" ? [line, max] : [min, line];
}

function modalLine(lines: readonly number[]): number {
  const counts = new Map<number, number>();
  for (const line of lines) counts.set(line, (counts.get(line) ?? 0) + 1);
  const top = Math.max(...counts.values());
  const modes = [...counts].filter(([, count]) => count === top).map(([line]) => line).sort((a, b) => a - b);
  const middle = Math.floor(modes.length / 2);
  return modes.length % 2 === 1 ? modes[middle] : (modes[middle - 1] + modes[middle]) / 2;
}

export function deriveRow(row: DiscrepancyRow, event: EventHeader, listed: ReadonlySet<string>, selected: ReadonlySet<string>): DerivedRow | null {
  const books = row.books
    .filter((book) => listed.has(book.book))
    .sort((a, b) => a.line - b.line || (a.book < b.book ? -1 : a.book > b.book ? 1 : 0))
    .map((book): DerivedBook => selected.has(book.book) ? { ...book, bettable: true } : { ...book, overLinks: NO_LINKS, underLinks: NO_LINKS, bettable: false });
  if (books.length < 2) return null;
  const min = books[0].line;
  const max = books[books.length - 1].line;
  if (max - min <= LINE_TOLERANCE) return null;
  const modal = modalLine(books.map((book) => book.line));
  const prizePicksPair = books.length === 2 && books.some((book) => book.book === PRIZEPICKS);
  const candidates = books.filter((book) => book.bettable && !(prizePicksPair && book.book === PRIZEPICKS));
  if (candidates.length === 0) return null;
  let bestBook = candidates[0];
  for (const book of candidates) {
    const distance = Math.abs(book.line - modal);
    const held = Math.abs(bestBook.line - modal);
    if (distance > held + LINE_TOLERANCE) bestBook = book;
  }
  if (Math.abs(bestBook.line - modal) <= LINE_TOLERANCE) return null;
  const side: DiscrepancySide = bestBook.line < modal ? "over" : "under";
  const [low, high] = edgeRange(min, max, bestBook.line, side);
  const odds = sideOdds(bestBook, side);
  return {
    row, event, books, modal, min, max, spread: Math.round((high - low) * 1000) / 1000, edgePct: spreadEdgePct(low, high),
    best: { book: bestBook, side, odds, links: sideLinks(bestBook, side) },
  };
}

export type RailMark = { readonly line: number; readonly position: number; readonly books: readonly DerivedBook[] };

export function railMarks(derived: DerivedRow): readonly RailMark[] {
  const span = derived.max - derived.min;
  const marks: RailMark[] = [];
  for (const book of derived.books) {
    const last = marks[marks.length - 1];
    if (last !== undefined && Math.abs(last.line - book.line) <= LINE_TOLERANCE) {
      marks[marks.length - 1] = { ...last, books: [...last.books, book] };
      continue;
    }
    marks.push({ line: book.line, position: span > 0 ? ((book.line - derived.min) / span) * 100 : 50, books: [book] });
  }
  return marks;
}

export type BookRole = "easiest over" | "easiest under" | "middle line";

export function bookRole(derived: DerivedRow, book: DiscrepancyBook): BookRole {
  if (Math.abs(book.line - derived.min) <= LINE_TOLERANCE) return "easiest over";
  if (Math.abs(book.line - derived.max) <= LINE_TOLERANCE) return "easiest under";
  return "middle line";
}

export function bookSide(derived: DerivedRow, book: DiscrepancyBook): DiscrepancySide {
  if (book.book === derived.best.book.book) return derived.best.side;
  return bookRole(derived, book) === "easiest under" ? "under" : "over";
}

export type DiscrepancySource = {
  readonly ids: readonly RowId[];
  readonly total: number;
  readonly comparable: number;
  readonly markets: readonly string[];
  readonly rowAt: (id: RowId) => DerivedRow | null;
};

export type FoldInput = {
  readonly view: DiscrepanciesView;
  readonly filters: DiscrepancyFilters;
  readonly listed: ReadonlySet<string>;
  readonly selected: ReadonlySet<string>;
  readonly hidden: ReadonlySet<string>;
  readonly search: string;
  readonly nowMs?: number;
};

export function hideFingerprint(row: Pick<DiscrepancyRow, "league" | "eventId" | "entity" | "marketKey">): string | null {
  return buildCanonicalFingerprint({ sport: row.league, eventId: row.eventId, entityId: row.entity.key, market: row.marketKey, line: null, side: "discrepancy" });
}

const nameOrder = new Intl.Collator("en", { sensitivity: "base", numeric: true });
const marketOrder = new Intl.Collator("en", { sensitivity: "base", numeric: true });

function compare(filters: DiscrepancyFilters): (a: DerivedRow, b: DerivedRow) => number {
  const sign = filters.order === "asc" ? 1 : -1;
  switch (filters.sort) {
    case "spread": return (a, b) => sign * (a.spread - b.spread) || a.event.startMs - b.event.startMs;
    case "start": return (a, b) => sign * (a.event.startMs - b.event.startMs) || b.edgePct - a.edgePct;
    case "player": return (a, b) => sign * nameOrder.compare(a.row.entity.name, b.row.entity.name) || a.event.startMs - b.event.startMs;
    default: return (a, b) => sign * (a.edgePct - b.edgePct) || b.spread - a.spread;
  }
}

export function foldDiscrepancies({ view, filters, listed, selected, hidden, search, nowMs = Date.now() }: FoldInput): DiscrepancySource {
  const derivedById = new Map<RowId, DerivedRow>();
  const markets = new Set<string>();
  const matches = searchMatcher(search);
  const wanted = new Set(filters.markets.map((market) => market.toLowerCase()));
  const minBooks = Math.max(2, filters.minBooks);
  const startsIn = gameTimeMatcher({ days: filters.days, within: filters.within, live: false }, new Date(nowMs));
  const kept: DerivedRow[] = [];
  let comparable = 0;
  for (const id of view.rowIds) {
    const row = view.rows.get(id);
    const event = row === undefined ? undefined : view.events.get(row.eventId);
    if (row === undefined || event === undefined) continue;
    if (FANTASY_POINTS_MARKET.test(`${row.marketKey} ${row.market}`)) continue;
    const derived = deriveRow(row, event, listed, selected);
    if (derived === null) continue;
    comparable += 1;
    markets.add(periodCase(row.market));
    if (startsIn !== null && !startsIn(event.startMs)) continue;
    if (derived.books.length < minBooks) continue;
    if (!filters.includeMultipliers && derived.books.some((book) => bookMultiplier(book) !== null)) continue;
    if (wanted.size > 0 && !wanted.has(row.market.toLowerCase())) continue;
    if (matches && !matches(row.entity.name, row.team, row.market, event.name)) continue;
    if (hidden.size > 0) {
      const fingerprint = hideFingerprint(row);
      if (fingerprint !== null && hidden.has(fingerprint)) continue;
    }
    derivedById.set(id, derived);
    kept.push(derived);
  }
  kept.sort(compare(filters));
  return {
    ids: kept.map((derived) => derived.row.id),
    total: view.rowIds.length,
    comparable,
    markets: [...markets].sort(marketOrder.compare),
    rowAt: (id) => derivedById.get(id) ?? null,
  };
}
